import React, {useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faPenToSquare,
    faTrash,
    faChevronUp,
    faChevronDown,
    faBolt,
    faClock
} from "@fortawesome/pro-regular-svg-icons";

import {
    post_RemoteManagement_set_charging_profile,
    post_RemoteManagement_clear_charging_profile
} from "../../api/Ocpp";
import {
    mapSetChargingProfileTo201,
    post_RemoteManagement_set_charging_profile as post_RemoteManagement_set_charging_profile_201,
    post_RemoteManagement_clear_charging_profile as post_RemoteManagement_clear_charging_profile_201,
} from "../../api/Ocpp201";
import { isOcpp201, isStationMaxProfile } from "../point/ocppPointHelpers";
import { getAllChargingProfilesForSite } from "../../api/ChargingSites";
import { useAuth } from "../../providers/AuthContext";
import ConfirmationModal from "../../components/ConfirmationModal";
import ModalBase from "../../components/ModalBase";
import PointSmartChargingWizard from "../site/PointSmartChargingWizard";
import toast from "react-hot-toast";
import LoadingSpinner from "../../components/LoadingSpinner";
import { asUtcIso } from "../../helpers/DateTime";
const PointSmartCharging = ({ chargingSite }) => {
    const { t } = useTranslation();
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [activeChargingPointId, setActiveChargingPointId] = useState(null);
    const [initialProfile, setInitialProfile] = useState(null);
    const { getAccessToken } = useAuth();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [profileToRemove, setProfileToRemove] = useState(null);
    const [loading, setLoading] = useState(false); 


    // Profiles stored *purely* as OCPP objects
    const [profiles, setProfiles] = useState([
      /*  {
            chargingPointPublicId: "R1eUy9hwXt6",
            chargingProfilePublicId: "gLFrSQjCiA9",
            csChargingProfiles: {
                stackLevel: 4,
                chargingProfilePurpose: "ChargePointMaxProfile",
                chargingProfileKind: "Recurring",
                recurrencyKind: "Daily",
                validFrom: "2025-11-18T00:00:00.000+00:00",
                validTo: "2025-11-18T23:59:00.000+00:00",
                chargingSchedule: {
                    startSchedule: "2025-11-18T00:00:00.000+00:00",
                    chargingRateUnit: "W",
                    chargingSchedulePeriod: [
                        { startPeriod: 0, limit: 200000 },
                        { startPeriod: 7200, limit: 150000 },
                        { startPeriod: 18000, limit: 200000 },
                        { startPeriod: 28800, limit: 100000 },
                        { startPeriod: 39600, limit: 200000 }
                    ]
                }
            }
        }*/
    ]);

    const [openRows, setOpenRows] = useState({});

    const toggleRow = (id) => {
        setOpenRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    useEffect(() => {
        loadProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [getAccessToken, chargingSite]);

    const loadProfiles = async () => {
        if (!chargingSite?.chargingSitePublicId) return;  
        setLoading(true);

            try {
                const token = await getAccessToken();
                const list = await getAllChargingProfilesForSite(
                    token,
                    chargingSite.chargingSitePublicId
                );
                const filtered = list.filter(
                    p => isStationMaxProfile(p?.csChargingProfiles?.chargingProfilePurpose)
                );
                setProfiles(filtered);
                console.log("Loaded profiles:", list);
            } catch (err) {
                console.error("Error loading profiles", err);
            } finally {
                setLoading(false);
            }
    };

    function secondsToHHMM(utcSeconds) {
            // Visa alltid 00:00 för UTC-midnatt
        if (utcSeconds === 0 || utcSeconds === 86400) {
                return "00:00";
            }

            const offsetMinutes = -new Date().getTimezoneOffset(); // t.ex. +60 i Sverige vintertid
            const utcMinutes = utcSeconds / 60;

            let localMinutes = utcMinutes + offsetMinutes;

            // Wrap runt dygn
            localMinutes = ((localMinutes % 1440) + 1440) % 1440;

            const hh = String(Math.floor(localMinutes / 60)).padStart(2, "0");
            const mm = String(localMinutes % 60).padStart(2, "0");

            return `${hh}:${mm}`;
        }



    function getReadableOcppPeriods(schedulePeriods) {
        const sorted = [...schedulePeriods].sort((a, b) => a.startPeriod - b.startPeriod);

        return sorted.map((p, index) => {
            const startSec = p.startPeriod;
            const endSec = sorted[index + 1]
                ? sorted[index + 1].startPeriod
                : 86400; // sista perioden → till midnatt

            return {
                start: secondsToHHMM(startSec),
                end: secondsToHHMM(endSec),
                limit: p.limit
            };
        });
    }


    // Convert OCPP → wizard format
    const convertOcppToWizard = (profile) => {
        const ocpp = profile.csChargingProfiles;
        const schedule = ocpp.chargingSchedule;

        const startDate = ocpp.validFrom.split("T")[0];
        const endDate = ocpp.validTo.split("T")[0];

        // Determine mode
        const periods = schedule.chargingSchedulePeriod;
        const allLimitsEqual =
            periods.length === 1 ||
            periods.every(p => p.limit === periods[0].limit);

        if (allLimitsEqual) {
            // ALLDAY
            return {
                id: profile.chargingProfilePublicId,
                mode: "allday",
                allDayPower: periods[0].limit / 1000,
                startDate,
                endDate,
                chargerMaxPower: periods[0].limit / 1000
            };
        }

        // PERIOD MODE
        const extracted = [];
        const maxLimit = Math.max(...periods.map(p => p.limit));

        for (let i = 0; i < periods.length; i++) {
            const p = periods[i];
            if (p.limit === maxLimit) continue;

            const next = periods.find(x => x.startPeriod > p.startPeriod);
            const endSec = next ? next.startPeriod : 86400;

            extracted.push({
                start: secondsToTime(p.startPeriod),
                end: secondsToTime(endSec),
                power: p.limit / 1000
            });
        }

        return {
            id: profile.chargingProfilePublicId,
            mode: "periods",
            startDate,
            endDate,
            chargerMaxPower: maxLimit / 1000,
            periods: extracted
        };
    };

    function secondsToTime(utcSeconds) {
        const utcMinutes = Math.floor(utcSeconds / 60);

        // JS ger offset i minuter: Sverige vinter = -60, sommar = -120
        const offsetMinutes = new Date().getTimezoneOffset();

        // Invers från timeToSec:
        const localMinutes = utcMinutes - offsetMinutes;

        // Wrap runt 24h så att t.ex -30 → 23:30 fungerar
        const minutesInDay = 24 * 60;
        const wrapped = ((localMinutes % minutesInDay) + minutesInDay) % minutesInDay;

        const hh = String(Math.floor(wrapped / 60)).padStart(2, "0");
        const mm = String(wrapped % 60).padStart(2, "0");

        return `${hh}:${mm}`;
    }


    const createNewProfile = (chargingPointPublicId) => {
        setActiveChargingPointId(chargingPointPublicId);
        setInitialProfile(null);
        setIsWizardOpen(true);
    };

    const editProfile = (profile) => {
        setActiveChargingPointId(profile.chargingPointPublicId);

        const wiz = convertOcppToWizard(profile);

        setInitialProfile({
            ...wiz,

            existingId: profile.chargingProfilePublicId,
            existingChargingProfilePublicId: profile.chargingProfilePublicId,
            existingStackLevel: profile.csChargingProfiles.stackLevel,

            // for summary view
            chargingProfilePublicId: profile.chargingProfilePublicId,
            stackLevel: profile.stackLevel
        });

        setIsWizardOpen(true);
    };


    const getPointOcppVersion = (chargingPointPublicId) => {
        const point = chargingSite?.chargingPoints?.find(
            (cp) => cp.chargingPointPublicId === chargingPointPublicId
        );
        return point?.ocppVersion;
    };

    const deleteProfile = async (chargingPointPublicId, chargingProfilePublicId) => {
        const profileToDelete = { chargingPointPublicId, chargingProfilePublicId };

        try {
            const accessToken = await getAccessToken();
            if (isOcpp201(getPointOcppVersion(chargingPointPublicId))) {
                await post_RemoteManagement_clear_charging_profile_201(accessToken, profileToDelete);
            } else {
                await post_RemoteManagement_clear_charging_profile(accessToken, profileToDelete);
            }

            setProfiles((prev) =>
                prev.filter((p) => p.chargingProfilePublicId !== chargingProfilePublicId)
            );

            toast.success(t("smartCharging.profileRemoved"));
        } catch (error) {
            console.error(error);
            toast.error(t("smartCharging.failedDeleteProfile"));
        }
    };


    const handleWizardSubmit = async (wizardData) => {
        const isEdit = !!wizardData.existingId;
        let stackLevel;
        let chargingProfilePublicId;

        const validFrom = asUtcIso(wizardData.startDate, 0, 0, 0);
        const validTo = asUtcIso(wizardData.endDate, 23, 59, 0);

        const cpProfiles = profiles.filter(
            p => p.chargingPointPublicId === activeChargingPointId
        );

        const otherProfiles = isEdit
            ? cpProfiles.filter(p => p.chargingProfilePublicId !== wizardData.existingId)
            : cpProfiles;

        const hasOverlap = otherProfiles.some(p => {
            const existingStart = new Date(p.csChargingProfiles.validFrom);
            const existingEnd = new Date(p.csChargingProfiles.validTo);
            const newStart = Date.parse(validFrom);
            const newEnd = Date.parse(validTo);

            return (newStart <= existingEnd && newEnd >= existingStart);
        });

        if (hasOverlap) {
            toast.error(t("smartCharging.overlap"));
            return;
        }

        if (isEdit) {
            stackLevel = wizardData.existingStackLevel;
            chargingProfilePublicId = wizardData.existingChargingProfilePublicId;
        } else {
            const used = cpProfiles
                .map(p => Number(p?.csChargingProfiles?.stackLevel))
                .filter(n => Number.isInteger(n) && n >= 2);

            let candidate = 2;
            while (used.includes(candidate)) candidate++;

            stackLevel = candidate;
            chargingProfilePublicId = undefined;
        }

        const ocppProfile = buildOcppFromWizard(wizardData, stackLevel);

        const fullOcppObject = {
            chargingPointPublicId: activeChargingPointId,
            ...(chargingProfilePublicId && { chargingProfilePublicId }),
            csChargingProfiles: ocppProfile
        };

        try {
            const accessToken = await getAccessToken();
            if (isOcpp201(getPointOcppVersion(activeChargingPointId))) {
                await post_RemoteManagement_set_charging_profile_201(
                    accessToken,
                    mapSetChargingProfileTo201(fullOcppObject)
                );
            } else {
                await post_RemoteManagement_set_charging_profile(accessToken, fullOcppObject);
            }
        } catch (error) {
            console.error(error);
            toast.error(error?.message || t("smartCharging.failedSaveProfile"));
            return;
        }

        await loadProfiles();

        toast.success(isEdit ? t("smartCharging.profileUpdated") : t("smartCharging.profileCreated"));

        setIsWizardOpen(false);
        setInitialProfile(null);
    };



    const buildOcppFromWizard = (data, stackLevel) => {
        const { mode, startDate, endDate } = data;

        const validFrom = `${startDate}T00:00:00.000+00:00`;
        const validTo = `${endDate}T23:59:00.000+00:00`;

        if (mode === "allday") {
            return {
                stackLevel: stackLevel,
                chargingProfilePurpose: "ChargePointMaxProfile",
                chargingProfileKind: "Recurring",
                recurrencyKind: "Daily",
                validFrom,
                validTo,
                chargingSchedule: {
                    startSchedule: validFrom,
                    chargingRateUnit: "W",
                    duration: 86400,
                    chargingSchedulePeriod: [
                        {
                            startPeriod: 0,
                            limit: Number(data.allDayPower) * 1000
                        }
                    ]
                }
            };
        }

        // PERIOD MODE
        const FULL = Number(data.chargerMaxPower) * 1000;
        const sorted = [...data.periods].sort(
            (a, b) => timeToSec(a.start) - timeToSec(b.start)
        );

        const schedule = [];

        const firstStart = timeToSec(sorted[0].start);
        if (firstStart > 0) {
            schedule.push({ startPeriod: 0, limit: FULL });
        }

        for (let i = 0; i < sorted.length; i++) {
            const p = sorted[i];
            const startSec = timeToSec(p.start);
            const endSec = timeToSec(p.end);
            schedule.push({ startPeriod: startSec, limit: Number(p.power) * 1000 });

            const next = sorted[i + 1];
            if (next) {
                const nextStart = timeToSec(next.start);
                if (nextStart > endSec) {
                    schedule.push({ startPeriod: endSec, limit: FULL });
                }
            }
        }

        const lastEnd = timeToSec(sorted[sorted.length - 1].end);
        if (lastEnd < 86400) {
            schedule.push({ startPeriod: lastEnd, limit: FULL });
        }

        return {
            stackLevel: stackLevel,
            chargingProfilePurpose: "ChargePointMaxProfile",
            chargingProfileKind: "Recurring",
            recurrencyKind: "Daily",
            validFrom,
            validTo,
            chargingSchedule: {
                startSchedule: validFrom,
                chargingRateUnit: "W",
                chargingSchedulePeriod: schedule
            }
        };
    };

    function timeToSec(timeString) {
        const [h, m] = timeString.split(":").map(Number);
        const local = new Date();
        local.setHours(h, m, 0, 0);
        return local.getUTCHours() * 3600 + local.getUTCMinutes() * 60;
    }



    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <LoadingSpinner text={t("smartCharging.loadingProfiles")} />
            </div>
        );
    }
    return (
        <>

            <div className="p-10 pb-10">
               <h1 className="text-2xl font-bold mb-3 ">{t("smartCharging.chargers")}</h1>

                <div className="space-y-6">
                    {chargingSite.chargingPoints?.map((cp) => {
                        const cpProfiles = profiles.filter(
                            (p) => p.chargingPointPublicId === cp.chargingPointPublicId
                        );
                        const maxLevel = Math.max(0, Math.min(Number(cp?.maxStackLevel ?? 0), 6) - 2);
                        return (
                            <div
                                key={cp.chargingPointPublicId}
                                className="border rounded-lg p-4 bg-white shadow-sm"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-bold flex items-center gap-3 ">
                                        <FontAwesomeIcon
                                            icon={faBolt}
                                            className="text-yellow-500"
                                        />
                                        {cp.chargingPointName}
                                        {cp.supportsSmartCharging === true && (
                                        <span className="px-2 py-0.5 text-sm rounded bg-mute-200 text-mute-700">
                                                {cpProfiles.length}/{maxLevel}
                                            </span>
                                        )}
                                        {cp.isOnline === false && (
                                            <p className="text-sm text-red-600 font-medium">
                                                {t("smartCharging.chargerOffline")}
                                            </p>
                                        )}
                                    </h2>

                                    {cp.supportsSmartCharging === false && (
                                        <p className="text-sm text-red-600 font-medium">
                                            {t("smartCharging.noProfileSupport")}
                                        </p>
                                    )}
                                    {(
                                        cpProfiles.length < maxLevel &&
                                        cp.supportsSmartCharging !== false &&
                                        cp.isOnline !== false
                                    ) && (
                                            <button
                                                className={`btn btn-primary ${cpProfiles.length >= maxLevel
                                                        ? "opacity-50 cursor-not-allowed"
                                                        : "hover:opacity-90"
                                                    }`}
                                                onClick={() =>
                                                    cpProfiles.length < maxLevel &&
                                                    createNewProfile(cp.chargingPointPublicId)
                                                }
                                            >
                                                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                                {t("smartCharging.newSchedule")}
                                            </button>
                                        )}
                                </div>

                                {cpProfiles.length === 0 ? (
                                    <div className="text-mute-500 italic">{t("smartCharging.noProfiles")}</div>
                                ) : (
                                    <div className="space-y-4">
                                        {cpProfiles.map((p) => {
                                            const ocpp = p.csChargingProfiles;
                                            const isOpen = openRows[p.chargingProfilePublicId] || false;

                                            return (
                                                <div
                                                    key={p.chargingProfilePublicId}
                                                    className="border rounded-xl bg-mute-50 shadow-sm"
                                                >
                                                    <div
                                                        className="flex justify-between items-center p-3 cursor-pointer hover:bg-mute-100"
                                                        onClick={() => toggleRow(p.chargingProfilePublicId)}
                                                    >
                                                        <div>
                                                            <div className="font-semibold text-lg ">
                                                                {ocpp.validFrom.split("T")[0]} –{" "}
                                                                {ocpp.validTo.split("T")[0]}
                                                            </div>
                                                            <div className="text-sm text-mute-600">
                                                                {t("smartCharging.schedulePeriods", {
                                                                    count: ocpp.chargingSchedule.chargingSchedulePeriod.length
                                                                })}
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center gap-4 text-mute-700">
                                                            {isOpen ? (
                                                                <FontAwesomeIcon icon={faChevronUp} />
                                                            ) : (
                                                                <FontAwesomeIcon icon={faChevronDown} />
                                                            )}

                                                            <button
                                                                className="text-blue-600 hover:text-blue-700"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    editProfile(p);
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faPenToSquare} />
                                                            </button>

                                                            <button
                                                                className="text-red-600 hover:text-red-700"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setProfileToRemove(p);
                                                                    setConfirmOpen(true);
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {isOpen && (() => {
                                                        const readable = getReadableOcppPeriods(
                                                            ocpp.chargingSchedule.chargingSchedulePeriod
                                                        );

                                                        return (
                                                            <div className="bg-mute-100 border-t p-4 text-sm space-y-2">
                                                                {readable.map((per, idx) => (
                                                                    <div
                                                                        key={`${p.chargingProfilePublicId}-period-${idx}`}
                                                                        className="p-1.5 rounded bg-white border text-sm"
                                                                    >
                                                                        <div className="grid grid-cols-5 gap-4 items-center ">

                                                                            {/* Tid */}
                                                                            <div className="flex items-center gap-2">
                                                                                <FontAwesomeIcon
                                                                                    icon={faClock}
                                                                                    className="text-mute-500 text-sm"
                                                                                />
                                                                                <span className="text-sm font-semibold">{per.start} – {per.end}</span>
                                                                            </div>

                                                                            {/* Effekt */}
                                                                            <div className="flex items-center gap-2">
                                                                                <FontAwesomeIcon
                                                                                    icon={faBolt}
                                                                                    className="text-yellow-500 text-sm"
                                                                                />
                                                                                <span className="text-sm font-semibold">{per.limit / 1000} kW</span>

                                                                            </div>

                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );



                                                    })()}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <ModalBase
                isOpen={isWizardOpen}
                onClose={() => setIsWizardOpen(false)}
                size="lg"
                className="overflow-y-auto max-h-[90vh]"
            >
                <PointSmartChargingWizard
                    chargingPointPublicId={activeChargingPointId}
                    initialProfile={initialProfile}
                    onSubmit={handleWizardSubmit}
                />
            </ModalBase>

            <ConfirmationModal
                isOpen={confirmOpen}
                title={t("smartCharging.removeProfile")}
                onClose={() => {
                    setConfirmOpen(false);
                    setProfileToRemove(null);
                }}
                onConfirm={() => {
                    if (profileToRemove) {
                        deleteProfile(
                            profileToRemove.chargingPointPublicId,
                            profileToRemove.chargingProfilePublicId
                        );
                    }

                    setConfirmOpen(false);
                    setProfileToRemove(null);
                }}
            />
        </>
    );
};

export default PointSmartCharging;
